import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, AlertCircle, CheckCircle, Lock, Mail } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import useTranslation from '../data/useTranslation';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getPasswordStrength = (password, t) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  
  if (score <= 1) return { label: t.register.strengthWeak, color: 'bg-red-500', width: '25%' };
  if (score <= 2) return { label: t.register.strengthMedium, color: 'bg-orange-500', width: '50%' };
  if (score <= 3) return { label: t.register.strengthGood, color: 'bg-yellow-500', width: '75%' };
  return { label: t.register.strengthStrong, color: 'bg-green-500', width: '100%' };
};

const SITUATION_KEYS = [
  'etudiant', 'jeune_diplome', 'recherche_emploi', 'salarie', 'manager',
  'dirigeant', 'freelance', 'entrepreneur', 'liberal', 'enseignant',
  'sante', 'artiste', 'reconversion', 'parent_foyer', 'retraite', 'autre'
];

export default function Register() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [tokenStatus, setTokenStatus] = useState({ loading: true, valid: false, error: null, email: '', pack: '' });
  const [formData, setFormData] = useState({
    prenom: '', nom: '', email: '', password: '', confirmPassword: '',
    situation: '', situation_autre: '', secteur: '', acceptCgu: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API}/api/register/verify/${token}`);
        const data = await response.json();
        if (data.valid) {
          setTokenStatus({ loading: false, valid: true, email: data.email, pack: data.pack, error: null });
          setFormData(prev => ({ ...prev, email: data.email || '' }));
        } else {
          setTokenStatus({ loading: false, valid: false, error: data.error, message: data.message });
        }
      } catch (err) {
        setTokenStatus({ loading: false, valid: false, error: 'network', message: t.common.error });
      }
    };
    if (token) verifyToken();
    else setTokenStatus({ loading: false, valid: false, error: 'missing', message: t.register.linkInvalid });
  }, [token]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError(t.register.passwordMismatch);
      return;
    }
    if (formData.password.length < 8) {
      setError(t.register.passwordTooShort);
      return;
    }
    if (!formData.acceptCgu) {
      setError(t.register.mustAcceptCgu);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          prenom: formData.prenom,
          nom: formData.nom,
          email: formData.email,
          password: formData.password,
          situation: formData.situation,
          situation_autre: formData.situation === 'autre' ? formData.situation_autre : null,
          secteur: formData.secteur,
          accept_cgu: formData.acceptCgu
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t.common.error);
      
      localStorage.setItem('bpm_access_token', data.access_token);
      localStorage.setItem('bpm_refresh_token', data.refresh_token);
      localStorage.setItem('bpm_user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (tokenStatus.loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t.register.verifyingLink}</p>
        </div>
      </div>
    );
  }
  
  if (!tokenStatus.valid) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-end mb-4"><LanguageSelector /></div>
          <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700">
            {tokenStatus.error === 'used' ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">{t.register.accountAlreadyActive}</h2>
                <p className="text-gray-400 mb-6">{tokenStatus.message}</p>
                <Link to="/login" className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors">{t.login.submit}</Link>
              </>
            ) : tokenStatus.error === 'expired' ? (
              <>
                <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">{t.register.linkExpired}</h2>
                <p className="text-gray-400 mb-6">{tokenStatus.message}</p>
                <a href="mailto:contact@bpm.com" className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors">{t.register.contactUs}</a>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">{t.register.linkInvalid}</h2>
                <p className="text-gray-400 mb-6">{tokenStatus.message}</p>
                <a href="mailto:contact@bpm.com" className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors">{t.register.contactUs}</a>
              </>
            )}
          </div>
          <div className="mt-6">
            <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm">\u2190 {t.common.backToHome}</Link>
          </div>
        </div>
      </div>
    );
  }
  
  const passwordStrength = getPasswordStrength(formData.password, t);
  
  return (
    <div className="min-h-screen bg-[#111827] py-12 px-4">
      <div className="w-full max-w-lg mx-auto">
        <div className="flex justify-end mb-4"><LanguageSelector /></div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold"><span className="text-orange-500">BPM</span></h1>
            <p className="text-gray-500 text-sm mt-1">Beyond Personal Matrix</p>
          </Link>
        </div>
        
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-green-400 font-medium">{t.register.paymentConfirmed}</p>
            <p className="text-green-300 text-sm">{t.register.paymentConfirmedSub}</p>
          </div>
        </div>
        
        <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6 text-center" data-testid="register-title">{t.register.title}</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">{t.register.firstName} *</label>
                <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder={t.register.firstNamePlaceholder} data-testid="register-prenom" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">{t.register.lastName} *</label>
                <input type="text" name="nom" value={formData.nom} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder={t.register.lastNamePlaceholder} data-testid="register-nom" />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">{t.register.email} *</label>
              <div className="relative">
                <input type="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full px-4 py-3 pl-10 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder={t.register.emailPlaceholder} data-testid="register-email" />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">{t.register.password} * ({t.register.passwordMin})</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required minLength={8}
                  className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors pr-12"
                  placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" data-testid="register-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{ width: passwordStrength.width }} />
                  </div>
                  <p className={`text-xs mt-1 ${passwordStrength.color.replace('bg-', 'text-')}`}>
                    {t.register.strength} : {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">{t.register.confirmPassword} *</label>
              <input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required
                className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" data-testid="register-confirm-password" />
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-400 text-xs mt-1">{t.register.passwordMismatch}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">{t.register.situation} *</label>
              <select name="situation" value={formData.situation} onChange={handleChange} required
                className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                data-testid="register-situation">
                <option value="">{t.register.situationPlaceholder}</option>
                {SITUATION_KEYS.map(key => (
                  <option key={key} value={key}>{t.register.situations[key]}</option>
                ))}
              </select>
            </div>
            
            {formData.situation === 'autre' && (
              <div>
                <label className="block text-gray-400 text-sm mb-2">{t.register.situationOtherLabel} *</label>
                <input type="text" name="situation_autre" value={formData.situation_autre} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder={t.register.situationOtherPlaceholder} data-testid="register-situation-autre" />
              </div>
            )}
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">{t.register.sector}</label>
              <select name="secteur" value={formData.secteur} onChange={handleChange}
                className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                data-testid="register-secteur">
                <option value="">{t.register.sectorPlaceholder}</option>
                {t.register.sectors.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-start gap-3">
              <input type="checkbox" name="acceptCgu" checked={formData.acceptCgu} onChange={handleChange} id="acceptCgu"
                className="mt-1 w-5 h-5 rounded border-gray-600 bg-[#111827] text-orange-500 focus:ring-orange-500" data-testid="register-cgu" />
              <label htmlFor="acceptCgu" className="text-gray-400 text-sm">
                {t.register.acceptCgu}{' '}
                <a href="/cgu" target="_blank" className="text-orange-500 hover:underline">{t.register.cguLink}</a>
                {' '}{t.register.andThe}{' '}
                <a href="/confidentialite" target="_blank" className="text-orange-500 hover:underline">{t.register.privacyLink}</a> *
              </label>
            </div>
            
            <button type="submit" disabled={loading || !formData.acceptCgu}
              className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
              data-testid="register-submit">
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  {t.register.submit}
                </>
              )}
            </button>
          </form>
        </div>
        
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm">\u2190 {t.common.backToHome}</Link>
        </div>
      </div>
    </div>
  );
}
